import { useEffect, useRef, useState } from "react";
import { ItemModel } from "../models/ItemModel";
import itemService from "../services/itemService";

interface EditSellingConditionsCompProps {
    show: boolean;
    item: ItemModel;
    onClose: () => void;
    onSellingConditionsUpdated: (updatedItem: ItemModel) => void;
}

interface SellingCondtionsFormErrors {
    minOfferQty?: string;
    availableRegion?: string;
}

export const AVAILABLE_REGIONS = [
    { name: "United Arab Emirates" },
    { name: "Qatar" },
    { name: "Saudi Arabia" },
    { name: "Bahrain" },
    { name: "Kuwait" },
    { name: "Pakistan" },
    { name: "India" },
    { name: "Ghana" },
    { name: "Nigeria" },
    { name: "South Africa" },
    { name: "Australia" },
    { name: "New Zealand" },
] as const;

const EditSellingConditionsComp: React.FC<EditSellingConditionsCompProps> = ({
    show,
    item,
    onClose,
    onSellingConditionsUpdated,
}) => {
    const modalRef = useRef<HTMLDivElement | null>(null);
    const bsModalRef = useRef<any>(null);

    // Change minOfferQty state to string | number for flexible input handling
    const [minOfferQty, setMinOfferQty] = useState<string | number>(item.minOfferQty ?? '');
    const [availableRegion, setAvailableRegion] = useState(item.availableRegion ?? '');

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [sellingConditionsFormErrors, setSellingConditionsFormErrors] = useState<SellingCondtionsFormErrors>({});
    const [serviceStatus, setServiceStatus] = useState({ type: '', message: '' });

    const [selectedRegions, setSelectedRegions] = useState<string[]>(availableRegion ? availableRegion.split(',').map(r => r.trim()) : []);

    const toggleRegion = (name: string) => {
        setSelectedRegions((prev) =>
            prev.includes(name)
                ? prev.filter((r) => r !== name) // remove if already selected
                : [...prev, name] // add if not selected
        );
        setAvailableRegion(
            selectedRegions.includes(name)
                ? selectedRegions.filter((r) => r !== name).sort().join(', ')
                : [...selectedRegions, name].sort().join(', ')
        );
    };

    useEffect(() => {
        if (modalRef.current) {
            bsModalRef.current = new (window as any).bootstrap.Modal(modalRef.current);
            modalRef.current.addEventListener('hidden.bs.modal', onClose);
        }
        return () => {
            modalRef.current?.removeEventListener('hidden.bs.modal', onClose);
        };
    }, []);

    useEffect(() => {
        if (show) {
            setMinOfferQty(item.minOfferQty ?? '');
            setAvailableRegion(item.availableRegion ?? '');
            setSelectedRegions(
                item.availableRegion
                    ? item.availableRegion.split(',').map(r => r.trim())
                    : []
            );
            setServiceStatus({ type: '', message: '' });

            bsModalRef.current?.show();
        } else {
            bsModalRef.current?.hide();
        }
    }, [show]);

    const validateSellingCondtions = () => {
        const errors: SellingCondtionsFormErrors = {};
        // Parse minOfferQty to number for validation
        const minOfferQtyNum = typeof minOfferQty === 'string' ? Number(minOfferQty) : minOfferQty;
        if (!minOfferQty || isNaN(minOfferQtyNum) || minOfferQtyNum <= 0) {
            errors.minOfferQty = "Minimum offer quantity is required and must be greater than 0";
        }
        
        setSellingConditionsFormErrors(errors);
        return Object.keys(errors).length === 0;
    }

    const handleSellingConditionsSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setServiceStatus({ type: "", message: "" });

        if (validateSellingCondtions()) {
            setIsSubmitting(true);
            try {
                const updatedItem = {
                    ...item,
                    minOfferQty: typeof minOfferQty === 'string' ? Number(minOfferQty) : minOfferQty,
                    availableRegion: availableRegion,
                };
                const updateSucceed = await itemService.updateItem(updatedItem);
                if (updateSucceed === "Success") {
                    onSellingConditionsUpdated(updatedItem);
                    setServiceStatus({ type: "success", message: "Selling conditions updated successfully." });
                } else {
                    setServiceStatus({ type: "error", message: "Failed to update selling conditions." });
                }
            } catch (error) {
                setServiceStatus({ type: "error", message: "Failed to update selling conditions." });
            } finally {
                setIsSubmitting(false);
            }
        }
    }

    return (
        <div className="modal fade" tabIndex={-1} ref={modalRef} data-bs-backdrop="static" data-bs-keyboard="false" >
            <div className="modal-dialog">
                <div className="modal-content rounded-0">
                    <form onSubmit={handleSellingConditionsSubmit}>
                        <div className="modal-header">
                            <h5 className="modal-title">Edit Selling Conditions</h5>
                            <button
                                type="button"
                                className="btn-close"
                                onClick={() => bsModalRef.current?.hide()}
                                disabled={isSubmitting}
                            ></button>
                        </div>
                        <div className="modal-body">
                            <div className="col-md-12 mb-3">
                                <label htmlFor="inputMinOfferQty" className="form-label">Minimum Offer Qty</label>
                                <input
                                    type="number"
                                    className={`form-control ${sellingConditionsFormErrors.minOfferQty ? 'is-invalid' : ''}`}
                                    min={1}
                                    step="1"
                                    id="inputMinOfferQty"
                                    value={minOfferQty === null ? '' : minOfferQty}
                                    onChange={(e) => setMinOfferQty(e.target.value === '' ? '' : Number(e.target.value))}
                                    disabled={isSubmitting}
                                />
                                {sellingConditionsFormErrors.minOfferQty && <div className="invalid-feedback">{sellingConditionsFormErrors.minOfferQty}</div>}
                            </div>
                            <div className="col-md-12">
                                <label className="form-label">Available Regions</label>
                                <div className={`d-flex flex-wrap gap-2 mb-2 ${sellingConditionsFormErrors.availableRegion ? "is-invalid border-danger" : ""
                                    }`}>
                                    {AVAILABLE_REGIONS.map((region) => {
                                        const isChecked = selectedRegions.includes(region.name);

                                        return (
                                            <div key={region.name} className="col-6">
                                                <div className="form-check">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        value={region.name}
                                                        id={`region-${region.name}`}
                                                        checked={isChecked}
                                                        onChange={() => toggleRegion(region.name)}
                                                    />
                                                    <label className="form-check-label" htmlFor={`region-${region.name}`}>
                                                        {region.name}
                                                    </label>
                                                </div>
                                            </div>
                                        );
                                    })}
                                    <div className="form-check  col-6">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id="region-all"
                                            checked={selectedRegions.length === AVAILABLE_REGIONS.length}
                                            onChange={() => {
                                                let regions: string[] = []
                                                if (selectedRegions.length === AVAILABLE_REGIONS.length) {
                                                    regions = [];
                                                } else {
                                                    regions = AVAILABLE_REGIONS.map(r => r.name);
                                                }

                                                setSelectedRegions(regions);
                                                setAvailableRegion(regions.sort().join(','));
                                            }}
                                        />
                                        <label className="form-check-label" htmlFor="region-all">
                                            All
                                        </label>
                                    </div>
                                </div>
                                {/* <textarea
                                    className={`form-control ${sellingConditionsFormErrors.availableRegion ? 'is-invalid' : ''}`}
                                    rows={5}
                                    value={availableRegion}
                                    onChange={(e) => setAvailableRegion(e.target.value)}
                                    disabled={isSubmitting}
                                ></textarea> */}
                                {sellingConditionsFormErrors.availableRegion && <div className="invalid-feedback">{sellingConditionsFormErrors.availableRegion}</div>}
                            </div>
                            {serviceStatus.type.toLowerCase() === "error" && (
                                <div className="col-md-12 mt-2">
                                    <div className="alert alert-danger p-2 mb-0">{serviceStatus.message}</div>
                                </div>
                            )}
                            {serviceStatus.type.toLowerCase() === "success" && (
                                <div className="col-md-12 mt-2">
                                    <div className="alert alert-success p-2 mb-0">{serviceStatus.message}</div>
                                </div>
                            )}
                        </div>
                        <div className="modal-footer d-flex justify-content-between">
                            <button
                                type="button"
                                className="btn btn-outline-dark rounded-0"
                                onClick={onClose}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn btn-dark rounded-0"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> Saving ...
                                    </>
                                ) : (
                                    "Save"
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default EditSellingConditionsComp;
